<template>
  <Teleport to="body">
    <div v-if="show" class="cms-modal-overlay" @click="closeModal">
      <div class="cms-modal cms-modal-large" @click.stop>
        <div class="cms-modal-header">
          <h3>{{ $t('cms.activityCategories.title') }}</h3>
          <button type="button" class="cms-modal-close-btn" @click="closeModal" :aria-label="$t('general.close')">
            <v-icon icon="close" size="md" />
          </button>
        </div>
      
      <div class="cms-modal-body">
        <!-- Existing Categories List -->
        <div class="cms-categories-list-section">
          <h4>{{ $t('cms.activityCategories.existingCategories') }}</h4>
          <div v-if="isLoading" class="cms-loading-spinner"></div>
          <div v-else-if="categories.length === 0" class="cms-empty-state">
            <p>{{ $t('cms.activityCategories.noCategories') }}</p>
          </div>
          <div v-else class="cms-categories-grid">
            <div 
              v-for="category in categories" 
              :key="category.id" 
              class="cms-category-card"
            >
              <div class="cms-category-icon">
                <img v-if="category.icon" :src="category.icon" :alt="category.name" />
                <v-icon v-else icon="tag" size="md" />
              </div>
              <div class="cms-category-info">
                <h5>{{ category.name }}</h5>
                <p class="cms-category-slug">{{ category.slug }}</p>
              </div>
              <div class="cms-category-actions">
                <cms-icon-button 
                  variant="primary" 
                  icon="pencil" 
                  size="sm"
                  :disabled="isEditing || isDeleting === category.id"
                  @click="startEditCategory(category)"
                />
                <cms-icon-button 
                  variant="danger" 
                  icon="trash" 
                  size="sm"
                  :disabled="isEditing || isDeleting === category.id"
                  @click="deleteCategory(category)"
                />
              </div>
            </div>
          </div>
        </div>
        
        <!-- Add/Edit Category Form -->
        <div class="cms-category-form">
          <h4>{{ isEditing ? $t('cms.activityCategories.editCategory') : $t('cms.activityCategories.addNew') }}</h4>
          <form @submit.prevent="isEditing ? updateCategory() : createCategory()">
            <div class="cms-form-grid">
              <div class="cms-form-group">
                <label>{{ $t('cms.activityCategories.slug') }} <span class="cms-required">*</span></label>
                <input 
                  v-model="newCategory.slug" 
                  type="text" 
                  class="cms-input"
                  :placeholder="$t('cms.activityCategories.slugPlaceholder')"
                  required
                />
              </div>
              <div class="cms-form-group">
                <label>{{ $t('cms.activityCategories.order') }}</label>
                <input 
                  v-model.number="newCategory.order" 
                  type="number" 
                  class="cms-input"
                  min="0"
                />
              </div>
            </div>
            
            
            <cms-translation-tabs :languages="languages" v-model="currentLanguage">
              <template #default="{ locale }">
                <div class="cms-form-group">
                  <label>
                    {{ $t('cms.activityCategories.name') }} <span class="cms-required">*</span>
                    ({{ locale.toUpperCase() }})
                  </label>
                  <input 
                    v-model="newCategory.translations[locale].name" 
                    type="text" 
                    class="cms-input"
                    required
                  />
                </div>
              </template>
            </cms-translation-tabs>
            
            <div class="cms-form-group">
              <label>{{ $t('cms.activityCategories.icon') }}</label>
              <cms-image-upload
                ref="iconUploadRef"
                v-model="newCategory.icon"
                :help-text="$t('cms.activityCategories.iconHelp')"
                :full-width="true"
                preview-size="icon"
                @uploaded="handleIconUploaded"
                @removed="handleIconRemoved"
              />
            </div>
            
            <div class="cms-form-group">
              <label class="cms-checkbox-item">
                <input v-model="newCategory.is_public" type="checkbox" class="cms-checkbox" />
                <span>{{ $t('cms.activityCategories.isPublic') }}</span>
              </label>
            </div>
            
            <div class="cms-modal-actions">
              <button v-if="isEditing" type="button" class="cms-btn cms-btn-secondary" @click="cancelEdit">
                {{ $t('general.button.cancel') }}
              </button>
              <button type="button" class="cms-btn cms-btn-secondary" @click="resetNewCategory">
                {{ $t('cms.activityCategories.reset') }}
              </button>
              <button type="submit" class="cms-btn cms-btn-primary" :disabled="isCreating || isUpdating">
                {{ (isCreating || isUpdating) ? $t('general.button.saving') : (isEditing ? $t('general.button.save') : $t('general.button.add')) }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import CmsImageUpload from '@/components/cms/CmsImageUpload.vue';
import CmsTranslationTabs from '@/components/cms/CmsTranslationTabs.vue';
import CmsIconButton from '@/components/cms/CmsIconButton.vue';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import useConcepts from '@/helpers/Concepts';
import { useAppLanguages } from '@/composables/useAppLanguages';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';
import { hasPendingCmsMediaSave, persistCmsMediaAttachment } from '@/helpers/cmsMediaUpload';

interface Category {
  id: number;
  name: string;
  slug: string;
  icon?: string;
  order: number;
  is_public: boolean;
  translations?: any[];
}

export default defineComponent({
  name: 'CmsCategoryModal',
  components: {
    VIcon,
    CmsImageUpload,
    CmsTranslationTabs,
    CmsIconButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'category-created', 'category-updated', 'category-deleted'],
  setup(props, { emit }) {
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();
    const concepts = useConcepts();
    const { languages: appLanguages, locales, resolveStoredLocale } = useAppLanguages();

    const buildEmptyCategoryTranslations = () =>
      Object.fromEntries(locales.value.map((locale) => [locale, { name: '' }]));

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const languages = computed(() =>
      appLanguages.value.map((lang: any) => ({
        value: lang.value || lang.key,
        label: lang.label || lang.name,
        key: lang.key || lang.value,
        shortName: lang.shortName || lang.value || lang.key,
      })),
    );
    const currentLanguage = ref<string>(resolveStoredLocale(localStorage.getItem('language')));
    
    const categories = ref<Category[]>([]);
    const isLoading = ref(false);
    const isCreating = ref(false);
    const isUpdating = ref(false);
    const isDeleting = ref<number | null>(null);
    const isEditing = ref(false);
    const editingCategoryId = ref<number | null>(null);
    const iconUploadRef = ref<any>(null);
    const iconMediaPayload = ref<any[] | null>(null);
    
    const CMS_MEDIA_MODEL_TYPE = 'cms_activity_category';
    const ICON_MEDIA_COLLECTION = 'cms_activity_category_icon';
    const MEDIA_DRIVER = 's3';
    
    const newCategory = reactive<{
      slug: string;
      order: number;
      icon: string;
      is_public: boolean;
      translations: Record<string, { name: string }>;
    }>({
      slug: '',
      order: 0,
      icon: '',
      is_public: true,
      translations: buildEmptyCategoryTranslations(),
    });

    watch(locales, () => {
      const empty = buildEmptyCategoryTranslations();
      newCategory.translations = {
        ...empty,
        ...newCategory.translations,
      };
    });
    
    const closeModal = () => {
      emit('close');
    };
    
    // Helper functions
    const extractListData = (data: any): any[] => {
      if (Array.isArray(data)) return data;
      if (data?.data) return Array.isArray(data.data) ? data.data : [data.data];
      if (data?.attributes) return [data];
      return [];
    };
    
    const getCollectionName = (item: any) => {
      if (!item) return "";
      return item.collection_name || item.collectionName || item.attributes?.collection_name || "";
    };
    
    const getMediaUrl = (item: any) => {
      if (!item) return "";
      const attributes = item.attributes || {};
      return (
        item.storage_url ||
        item.storageUrl ||
        attributes.storage_url ||
        attributes.storageUrl ||
        item.original_url ||
        item.originalUrl ||
        attributes.original_url ||
        attributes.originalUrl ||
        item.url ||
        attributes.url ||
        item.preview_url ||
        item.previewUrl ||
        attributes.preview_url ||
        attributes.previewUrl ||
        ""
      );
    };
    
    const loadCategories = async () => {
      if (!agencySlug.value) return;
      
      isLoading.value = true;
      try {
        const response = await api.activityCategoryService.listCmsActivityCategories(agencySlug.value, {
          filters: {},
        });
        
        const list = extractListData(response);
        
        categories.value = list.map((cat: any) => {
          const normalized = normalizeResource(cat) || cat;
          const translations = extractListData(normalized.translations || normalized.attributes?.translations || []);
          const translation = translations.find((t: any) => 
            (t.locale || '').toLowerCase() === currentLanguage.value.toLowerCase()
          ) || translations[0] || {};
          
          // Extract icon from media relationship
          const mediaRelationship = extractRelationship(cat, 'media');
          const normalizedMedia = normalizeResource(mediaRelationship);
          const mediaItems = Array.isArray(normalizedMedia) ? normalizedMedia : (normalizedMedia ? [normalizedMedia] : []);
          
          // Find icon media in the cms_activity_category_icon collection
          const iconMedia = mediaItems.find((media: any) => {
            const normalizedMediaItem = normalizeResource(media) || media;
            const collectionName = getCollectionName(normalizedMediaItem);
            return collectionName === 'cms_activity_category_icon';
          });
          
          const iconUrl = iconMedia ? getMediaUrl(iconMedia) : '';
          
          return {
            id: normalized.id || normalized.attributes?.id,
            name: translation.name || normalized.attributes?.name || normalized.name || '',
            slug: normalized.slug || normalized.attributes?.slug || '',
            icon: iconUrl,
            order: normalized.order || normalized.attributes?.order || 0,
            is_public: normalized.is_public ?? normalized.attributes?.is_public ?? true,
            translations,
          };
        }).sort((a: Category, b: Category) => a.order - b.order);
      } catch (error: any) {
        console.error('Failed to load categories', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        isLoading.value = false;
      }
    };
    
    const resetNewCategory = () => {
      newCategory.slug = '';
      newCategory.order = 0;
      newCategory.icon = '';
      newCategory.is_public = true;
      newCategory.translations = buildEmptyCategoryTranslations();
      iconMediaPayload.value = null;
      iconUploadRef.value?.clearMedia?.();
      isEditing.value = false;
      editingCategoryId.value = null;
    };
    
    const cancelEdit = () => {
      resetNewCategory();
    };
    
    const startEditCategory = async (category: Category) => {
      if (!agencySlug.value) return;
      
      isEditing.value = true;
      editingCategoryId.value = category.id;
      
      // Load full category data with translations
      try {
        const response = await api.activityCategoryService.getCmsActivityCategory(agencySlug.value, category.id);
        const normalized = normalizeResource(response) || response;
        const attributes = normalized.attributes || normalized;
        
        // Extract translations
        const translationsList = extractListData(normalized.translations || attributes.translations || []);
        const translationsObj: Record<string, { name: string }> = {};
        
        languages.value.forEach((lang: any) => {
          const locale = lang.key || lang.value;
          const translation = translationsList.find((t: any) => 
            (t.locale || '').toLowerCase() === locale.toLowerCase()
          );
          translationsObj[locale] = {
            name: translation?.name || attributes.name || category.name || '',
          };
        });
        
        // Extract icon from media relationship
        const mediaRelationship = extractRelationship(response, 'media');
        const normalizedMedia = normalizeResource(mediaRelationship);
        const mediaItems = Array.isArray(normalizedMedia) ? normalizedMedia : (normalizedMedia ? [normalizedMedia] : []);
        
        const iconMedia = mediaItems.find((media: any) => {
          const normalizedMediaItem = normalizeResource(media) || media;
          const collectionName = getCollectionName(normalizedMediaItem);
          return collectionName === 'cms_activity_category_icon';
        });
        
        const iconUrl = iconMedia ? getMediaUrl(iconMedia) : '';
        
        // Populate form with category data
        newCategory.slug = attributes.slug || normalized.slug || category.slug || '';
        newCategory.order = attributes.order ?? normalized.order ?? category.order ?? 0;
        newCategory.icon = iconUrl;
        newCategory.is_public = attributes.is_public ?? normalized.is_public ?? category.is_public ?? true;
        newCategory.translations = translationsObj;
        
        // Set existing icon in upload component if available
        if (iconUrl && iconUploadRef.value) {
          // The icon upload component might need the existing media item
          // This depends on how CmsImageUpload handles existing images
        }
      } catch (error: any) {
        console.error('Failed to load category for editing', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
        cancelEdit();
      }
    };
    
    const buildMediaConfirmEntries = (mediaItems: any[] = [], collection: string) => {
      return (Array.isArray(mediaItems) ? mediaItems : [])
        .map((item: any, index: number) => {
          const attributes = item?.attributes ?? {};
          if (attributes.isExisting) {
            return null;
          }

          const uuid = attributes.uuid ?? item.uuid;
          if (!uuid) {
            return null;
          }

          const order = attributes.order ?? attributes.order_column ?? item.order ?? index + 1;

          return {
            uuid,
            name: attributes.name ?? item.name ?? `media-${order}`,
            order,
            collection,
          };
        })
        .filter((entry): entry is { uuid: string; name: string; order: number; collection: string } => !!entry);
    };
    
    const handleIconUploaded = (media: any[] = []) => {
      iconMediaPayload.value = Array.isArray(media) ? [...media] : [];
    };
    
    const handleIconRemoved = (media: any[] = []) => {
      iconMediaPayload.value = Array.isArray(media) ? [...media] : [];
    };
    
    const confirmIconMedia = async (categoryId: number) => {
      const payload = iconMediaPayload.value ?? [];
      const hasUploads = hasPendingCmsMediaSave(payload, ICON_MEDIA_COLLECTION);

      if (!hasUploads) {
        return;
      }

      try {
        await persistCmsMediaAttachment({
          api,
          driver: MEDIA_DRIVER,
          agencySlug: agencySlug.value!,
          mediaItems: payload,
          modelType: CMS_MEDIA_MODEL_TYPE,
          modelId: categoryId,
          collection: ICON_MEDIA_COLLECTION,
        });

        iconMediaPayload.value = null;
        iconUploadRef.value?.clearMedia?.();
      } catch (error) {
        console.error('Error confirming icon media:', error);
        throw error;
      }
    };
    
    const createCategory = async () => {
      if (!agencySlug.value) return;
      
      isCreating.value = true;
      try {
        const translations = Object.keys(newCategory.translations).map((locale) => ({
          locale,
          name: newCategory.translations[locale as keyof typeof newCategory.translations].name,
        }));
        
        const payload: any = {
          slug: newCategory.slug,
          order: newCategory.order || 0,
          is_public: newCategory.is_public,
          translations,
        };
        
        const response = await api.activityCategoryService.createCmsActivityCategory(agencySlug.value, payload);
        
        // Extract category ID from response
        const categoryId = response?.id || response?.data?.id || response?.data?.data?.id;
        
        // Handle icon upload confirmation if provided
        if (categoryId && iconMediaPayload.value && iconMediaPayload.value.length > 0) {
          try {
            await confirmIconMedia(categoryId);
          } catch (error: any) {
            console.error('Failed to confirm icon media:', error);
            toast.error(error?.response?.data?.message || t('general.errorMessage'));
          }
        }
        
        toast.success(t('cms.activityCategories.createSuccess'));
        resetNewCategory();
        await loadCategories();
        emit('category-created', response);
      } catch (error: any) {
        console.error('Failed to create category', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        isCreating.value = false;
      }
    };
    
    const updateCategory = async () => {
      if (!agencySlug.value || !editingCategoryId.value) return;
      
      isUpdating.value = true;
      try {
        const translations = Object.keys(newCategory.translations).map((locale) => ({
          locale,
          name: newCategory.translations[locale as keyof typeof newCategory.translations].name,
        }));
        
        const payload: any = {
          slug: newCategory.slug,
          order: newCategory.order || 0,
          is_public: newCategory.is_public,
          translations,
        };
        
        const response = await api.activityCategoryService.updateCmsActivityCategory(
          agencySlug.value,
          editingCategoryId.value,
          payload
        );
        
        // Handle icon upload confirmation if provided
        if (iconMediaPayload.value && iconMediaPayload.value.length > 0) {
          try {
            await confirmIconMedia(editingCategoryId.value);
          } catch (error: any) {
            console.error('Failed to confirm icon media:', error);
            toast.error(error?.response?.data?.message || t('general.errorMessage'));
          }
        }
        
        toast.success(t('cms.activityCategories.updateSuccess'));
        resetNewCategory();
        await loadCategories();
        emit('category-updated', response);
      } catch (error: any) {
        console.error('Failed to update category', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        isUpdating.value = false;
      }
    };
    
    const deleteCategory = async (category: Category) => {
      if (!agencySlug.value) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) {
        return;
      }
      
      isDeleting.value = category.id;
      try {
        await api.activityCategoryService.deleteCmsActivityCategory(agencySlug.value, category.id);
        toast.success(t('cms.activityCategories.deleteSuccess'));
        await loadCategories();
        emit('category-deleted', category);
      } catch (error: any) {
        console.error('Failed to delete category', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        isDeleting.value = null;
      }
    };
    
    watch(() => props.show, (show) => {
      if (show && agencySlug.value) {
        loadCategories();
      }
    });
    
    return {
      categories,
      isLoading,
      isCreating,
      isUpdating,
      isDeleting,
      isEditing,
      newCategory,
      languages,
      currentLanguage,
      closeModal,
      resetNewCategory,
      cancelEdit,
      createCategory,
      updateCategory,
      startEditCategory,
      deleteCategory,
      iconUploadRef,
      handleIconUploaded,
      handleIconRemoved,
    };
  },
});
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

.cms-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
  box-sizing: border-box;
}

.cms-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: calc(100vh - 4rem);
  overflow: hidden;
  color: #333;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  margin: auto;
  position: relative;
  min-height: 200px;
}

.cms-modal-large {
  max-width: 800px;
}

.cms-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
  background: white;
  position: sticky;
  top: 0;
  z-index: 100;
  min-height: 60px;
  box-sizing: border-box;
  
  h3 {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 600;
    color: #333;
    flex: 1;
    line-height: 1.4;
  }
  
  .cms-modal-close-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    background: rgba(0, 0, 0, 0.05);
    color: #666;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    margin-left: 1rem;
    
    &:hover {
      background: rgba(0, 0, 0, 0.1);
      color: #333;
      transform: scale(1.05);
    }
    
    &:active {
      background: rgba(0, 0, 0, 0.15);
      transform: scale(0.95);
    }
    
    :deep(.icon) {
      width: 20px;
      height: 20px;
      display: block;
    }
  }
}

.cms-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  max-height: calc(82vh - 12rem);
}

.cms-category-form {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid $cms-border-color;
  
  h4 {
    margin: 0 0 1.5rem 0;
    font-size: 1.1rem;
    color: $cms-text-primary;
  }
}

.cms-categories-list-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid $cms-border-color;
  
  h4 {
    margin: 0 0 1rem 0;
    font-size: 1.1rem;
    color: $cms-text-primary;
  }
}

.cms-categories-list {
  h4 {
    margin: 0 0 1rem 0;
    font-size: 1.1rem;
    color: $cms-text-primary;
  }
}

.cms-categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.cms-category-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: $cms-background-gray-light;
  border: 1px solid $cms-border-color;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: $cms-primary-color;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.cms-category-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $cms-background-overlay;
  border-radius: 8px;
  flex-shrink: 0;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
  
  :deep(.icon) {
    color: $cms-text-secondary;
  }
}

.cms-category-info {
  flex: 1;
  min-width: 0;
  
  h5 {
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    color: $cms-text-primary;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .cms-category-slug {
    margin: 0;
    font-size: 0.85rem;
    color: $cms-text-secondary;
  }
}

.cms-category-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.cms-modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

@media (max-width: 768px) {
  .cms-modal {
    width: 95%;
    max-width: 95%;
    margin: 1rem auto;
    max-height: 90vh;
  }
  
  .cms-modal-header {
    padding: 1rem;
    
    h3 {
      font-size: 1.1rem;
    }
  }
  
  .cms-modal-body {
    padding: 1rem;
  }
  
  .cms-categories-grid {
    grid-template-columns: 1fr;
  }
}
</style>

